/**
 * The answers to „Aký typ stavby?“ (`KLQ-20261023-011` §4) — data, not a code path.
 *
 * Each entry is one option on the wizard's first step. What the wizard does with it is read
 * off the entry and nowhere else: `rows` is the level plan it starts from, and `asksObjects`
 * decides whether the person walks the long path (phase → objects → levels → drawings) or goes
 * straight to the server's check. **No `if (template === …)` belongs anywhere outside this
 * file** — `KLQ-20261028-001` adds Bytový dom and fixes every shape from the co-founder's
 * input by editing this list, and a branch on an id in the wizard is a branch it would miss.
 *
 * The shapes here are provisional (owner decision 2): a family house is one building on three
 * floors, 1.PP–2.NP, and *Other* starts with nothing filled in. Nothing produces the 27 levels
 * the old `INITIAL_ROWS` did — three blocks of eight floors is a residential complex, not a
 * default.
 *
 * Schemes are seeds, for `EMPTY_ROW`'s reason: the levels step corrects an untouched row to
 * the project's country once `GET /numbering-schemes` answers.
 */

// `EMPTY_ROW` from its leaf module, not the drawings barrel: these templates read it at module
// evaluation, and the barrel sits on a cycle back through this feature (`DrawingsPage` imports
// `AddObjectForm`). Through the barrel the app died on load — `drawings/rows.ts` says why.
import type { Row } from '@/features/drawings';
import { EMPTY_ROW } from '@/features/drawings/rows';
import type { MessageKeys } from '@/lib/i18n';

type Key = MessageKeys['projectSetup'];

export interface StructureTemplate {
  readonly id: string;
  readonly labelKey: Key;
  readonly hintKey: Key;
  readonly rows: readonly Row[];
  /** Walk phase → objects → levels → drawings before the check, rather than going straight. */
  readonly asksObjects: boolean;
}

const FAMILY_HOUSE: StructureTemplate = {
  id: 'family-house',
  labelKey: 'template.familyHouse',
  hintKey: 'template.familyHouse.hint',
  rows: [
    { ...EMPTY_ROW, kind: 'building', count: 1, scheme: 'letters' },
    { ...EMPTY_ROW, kind: 'floor', count: 3, scheme: 'floors_sk', belowGrade: 1 },
  ],
  asksObjects: false,
};

const OTHER: StructureTemplate = {
  id: 'other',
  labelKey: 'template.other',
  hintKey: 'template.other.hint',
  // One blank row rather than none: `LevelPlanFields` always draws at least one, and a count
  // of 0 is the form's own "nothing yet".
  rows: [EMPTY_ROW],
  asksObjects: true,
};

/** The options, in the order the question lists them. */
export const STRUCTURE_TEMPLATES: readonly StructureTemplate[] = [FAMILY_HOUSE, OTHER];

/** The one preselected: the commonest answer, one click away from the check. */
export const DEFAULT_TEMPLATE: StructureTemplate = FAMILY_HOUSE;
